// Pure geometry for the floating DocuAI surface. All values come from the Figma
// "AI Chat area Reposition/Resize" section; see the hook for how they are applied.

export const LAUNCHER_HEIGHT = 66;
export const LAUNCHER_MIN_WIDTH = 336;
export const CHAT_MIN_WIDTH = 336;
export const CHAT_MIN_HEIGHT = 358;
export const DOCK_INSET_BOTTOM = 32;

const GROWN_HEIGHT_MIN = 660;
const GROWN_HEIGHT_MAX = 760;

export type Rect = { x: number; y: number; width: number; height: number };
export type Size = { width: number; height: number };
export type Point = { x: number; y: number };
export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export type DockInsets = { right: number };

/** Default inset between a dock column's right edge and its content (the layout right panel padding). */
export const DEFAULT_DOCK_INSETS: DockInsets = { right: 12 };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** Shrinks the rect to the bounds if needed, then moves it so it is fully inside. */
export const clampRectToBounds = (rect: Rect, bounds: Rect): Rect => {
  const width = Math.min(rect.width, bounds.width);
  const height = Math.min(rect.height, bounds.height);

  return {
    width,
    height,
    x: clamp(rect.x, bounds.x, bounds.x + bounds.width - width),
    y: clamp(rect.y, bounds.y, bounds.y + bounds.height - height),
  };
};

/**
 * Docked launcher: fixed 336px width, right edge flush with the column content's right edge, with the
 * 32px Figma bottom inset. Sidebar width changes only move it horizontally; they do not stretch it.
 */
export const getDockedLauncherRect = (column: Rect, insets: DockInsets = DEFAULT_DOCK_INSETS): Rect => {
  const width = LAUNCHER_MIN_WIDTH;

  return {
    x: column.x + column.width - insets.right - width,
    y: column.y + column.height - DOCK_INSET_BOTTOM - LAUNCHER_HEIGHT,
    width,
    height: LAUNCHER_HEIGHT,
  };
};

/**
 * Open-chat size: docked chats that were never resized match the fixed launcher width (336); otherwise
 * the stored size is used with the usual minimums. A chat whose transcript has at some point outgrown it
 * (`hasOverflowed` is sticky, not a live measurement) grows to the dynamic height, until the user picks a
 * size of their own.
 */
export const resolveOpenSize = (size: Size, options: { docked: boolean; customSize: boolean; hasOverflowed: boolean }): Size => {
  const minHeight = options.hasOverflowed && !options.customSize ? getGrownOpenHeight() : CHAT_MIN_HEIGHT;
  const height = Math.max(minHeight, size.height);
  if (options.docked && !options.customSize) {
    return { width: LAUNCHER_MIN_WIDTH, height };
  }
  return { width: Math.max(CHAT_MIN_WIDTH, size.width), height };
};

/** Height an overflowing chat grows to: 80% of the viewport, within the Figma range. */
const getGrownOpenHeight = () => clamp(Math.round(window.innerHeight * 0.8), GROWN_HEIGHT_MIN, GROWN_HEIGHT_MAX);

/**
 * Opens the chat upward from the launcher's bottom edge; when it does not fit above, the clamp shifts it
 * down into view, so open always grows up and collapse always shrinks down. The clamp moves only what is
 * on screen — the launcher it was derived from is untouched, so the chat comes back to it once the bounds
 * allow. Callers should pass a size from `resolveOpenSize`.
 */
export const resolveOpenRect = (launcher: Rect, size: Size, bounds: Rect): Rect => {
  const { width, height } = size;
  const y = launcher.y + launcher.height - height;

  return clampRectToBounds({ x: launcher.x, y, width, height }, bounds);
};

/**
 * How a drag or a resize records a free-floating chat's launcher: on the surface's bottom-left. It is the
 * inverse of `resolveOpenRect` whenever that placement was not clamped, so a chat dropped or resized where
 * it fits reopens on exactly the rect the gesture left it on.
 */
export const getLauncherPointForOpenRect = (rect: Rect): Point => ({
  x: rect.x,
  y: rect.y + rect.height - LAUNCHER_HEIGHT,
});

/** Resizes from one edge or corner keeping the opposite edge/corner anchored, within bounds and minimums. */
export const resizeRectFromEdge = (start: Rect, edge: ResizeEdge, dx: number, dy: number, bounds: Rect): Rect => {
  let { x, y } = start;
  let right = start.x + start.width;
  let bottom = start.y + start.height;

  if (edge.includes('e')) {
    right = clamp(start.x + start.width + dx, x + CHAT_MIN_WIDTH, bounds.x + bounds.width);
  }
  if (edge.includes('w')) {
    // Bounds narrower than the minimum width leave no room for it: pin to the bounds edge and let the
    // width fall to the bounds width, as clampRectToBounds does, instead of leaving the bounds.
    x = clamp(start.x + dx, bounds.x, Math.max(bounds.x, right - CHAT_MIN_WIDTH));
  }
  if (edge.includes('s')) {
    bottom = clamp(start.y + start.height + dy, y + CHAT_MIN_HEIGHT, bounds.y + bounds.height);
  }
  if (edge.includes('n')) {
    y = clamp(start.y + dy, bounds.y, Math.max(bounds.y, bottom - CHAT_MIN_HEIGHT));
  }

  return { x, y, width: right - x, height: bottom - y };
};

/**
 * A dragged surface docks only when it is near the slot at the bottom of the right panel: it overlaps
 * the column by at least a third of the column width, and its bottom edge is no more than one launcher
 * height above the slot's top edge. Hovering the top of the sidebar is not a drop. Wide open chats
 * often have their center left of the column; overlap is what matters, not the center point.
 */
export const isDockCandidate = (rect: Rect, column: Rect | null) => {
  if (!column) return false;

  const overlapLeft = Math.max(rect.x, column.x);
  const overlapRight = Math.min(rect.x + rect.width, column.x + column.width);
  const overlapsColumn = overlapRight - overlapLeft >= column.width / 3;

  const slotTop = column.y + column.height - DOCK_INSET_BOTTOM - LAUNCHER_HEIGHT;
  const nearSlot = rect.y + rect.height >= slotTop - LAUNCHER_HEIGHT;

  return overlapsColumn && nearSlot;
};
